import React, { useState, useMemo } from 'react';
import { useCoachesManagement, Organisation, CoachWithSubscription } from '../../hooks/useCoachesManagement';
import {
  HiArrowPath,
  HiMagnifyingGlass,
  HiExclamationCircle,
  HiUsers,
  HiAcademicCap,
  HiUserGroup,
  HiBuildingOffice,
  HiChevronUp,
  HiChevronDown,
  HiChevronUpDown
} from 'react-icons/hi2';
import { HiCash } from 'react-icons/hi';
import CoachDetailsModal from '../../components/CoachDetailsModal';

type SortField = 'name' | 'organisation' | 'members' | null;
type SortDirection = 'asc' | 'desc';

const CoachesCornerManagement: React.FC = () => {
  const { coaches, loading, error, organisations, refreshData } = useCoachesManagement();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCoach, setSelectedCoach] = useState<CoachWithSubscription | null>(null);
  const [sortField, setSortField] = useState<SortField>('organisation');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Helper function to get organization for a coach
  const getCoachOrganisation = (coachId: string): Organisation | null => {
    return organisations.find(org =>
      org.ownerId === coachId
    ) || null;
  };

  // Filter and sort coaches
  const filteredCoaches = useMemo(() => {
    let result = coaches.filter(coach =>
      coach.userData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.userData.surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.userData.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (coach.subscription?.subscriptionType || 'none').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortField) {
      result = [...result].sort((a, b) => {
        let comparison = 0;

        switch (sortField) {
          case 'name': {
            const nameA = `${a.userData.name} ${a.userData.surname || ''}`.toLowerCase();
            const nameB = `${b.userData.name} ${b.userData.surname || ''}`.toLowerCase();
            comparison = nameA.localeCompare(nameB);
            break;
          }
          case 'organisation': {
            const orgA = getCoachOrganisation(a.id);
            const orgB = getCoachOrganisation(b.id);
            // Coaches with organisations come first when ascending
            if (orgA && !orgB) comparison = -1;
            else if (!orgA && orgB) comparison = 1;
            else if (orgA && orgB) comparison = orgA.name.localeCompare(orgB.name);
            else comparison = 0;
            break;
          }
          case 'members': {
            const orgA = getCoachOrganisation(a.id);
            const orgB = getCoachOrganisation(b.id);
            const membersA = orgA?.members.length || 0;
            const membersB = orgB?.members.length || 0;
            comparison = membersA - membersB;
            break;
          }
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [coaches, searchTerm, sortField, sortDirection, organisations]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for a column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <HiChevronUpDown className="w-4 h-4 text-base-content/50" />;
    }
    return sortDirection === 'asc'
      ? <HiChevronUp className="w-4 h-4 text-info" />
      : <HiChevronDown className="w-4 h-4 text-info" />;
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';

    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.warn('Date formatting error:', error, date);
      return 'Invalid Date';
    }
  };

  if (loading && coaches.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg mb-4"></div>
          <p className="text-base-content/70">Loading coaches...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h1 className="text-3xl font-semibold">Coaches Management</h1>
        </div>
        <button
          onClick={refreshData}
          disabled={loading}
          className="btn btn-primary btn-sm"
        >
          <HiArrowPath className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert bg-red-500/15 border-none mb-6 text-red-600 dark:text-red-400">
          <HiExclamationCircle className="w-5 h-5" />
          <span>{error.message}</span>
        </div>
      )}

      {/* Search and Stats */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative">
          <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 w-5 h-5" />
          <input
            type="text"
            placeholder="Search coaches by name, email, UID, or subscription..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input input-bordered pl-10 w-full md:w-96"
          />
        </div>

        <div className="flex items-center space-x-4 text-sm text-base-content/70">
          <div className="flex items-center space-x-1">
            <HiUsers className="w-4 h-4" />
            <span>Total: {coaches.length}</span>
          </div>
          <div className="flex items-center space-x-1">
            <span>Showing: {filteredCoaches.length}</span>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card bg-base-100 shadow-sm border">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-info">
              {coaches.length}
            </div>
            <div className="text-sm text-base-content/70 flex items-center gap-1">
              <HiAcademicCap className="w-4 h-4" />
              Total Coaches
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-warning">
              {organisations.length}
            </div>
            <div className="text-sm text-base-content/70 flex items-center gap-1">
              <HiBuildingOffice className="w-4 h-4" />
              Organisations
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-success">
              {organisations.reduce((total, org) => total + org.members.length, 0)}
            </div>
            <div className="text-sm text-base-content/70 flex items-center gap-1">
              <HiUserGroup className="w-4 h-4" />
              Member Athletes
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow-sm border">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-info">
              ${coaches.filter(c => c.subscription?.subscriptionType === 'stripe').length * 50}
            </div>
            <div className="text-sm text-base-content/70 flex items-center gap-1">
              <HiCash className="w-4 h-4" />
              Est. MRR
            </div>          
          </div>
        </div>
      </div>

      {/* Coaches Table */}
      <div className="card bg-base-100 shadow-sm border border-base-200">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr className="bg-base-200">
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Coach
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200 select-none"
                    onClick={() => handleSort('organisation')}
                  >
                    <div className="flex items-center gap-1">
                      Organisation
                      {getSortIcon('organisation')}
                    </div>
                  </th>
                  <th className="font-semibold">Email</th>
                  <th className="font-semibold">Subscription Issued By</th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200 select-none"
                    onClick={() => handleSort('members')}
                  >
                    <div className="flex items-center gap-1">
                      Members
                      {getSortIcon('members')}
                    </div>
                  </th>
                  <th className="font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoaches.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-base-content/60">
                      {searchTerm ? 'No coaches found matching your search.' : 'No coaches found.'}
                    </td>
                  </tr>
                ) : (
                  filteredCoaches.map((coach) => (
                    <tr
                      key={coach.id}
                      className="hover:bg-base-200 cursor-pointer"
                      onClick={() => setSelectedCoach(coach)}
                    >
                      <td>
                        <div className="flex items-center space-x-3">
                          {coach.userData.imageUrlTiny || coach.userData.imageUrl ? (
                            <img
                              src={coach.userData.imageUrlTiny || coach.userData.imageUrl}
                              alt={`${coach.userData.name}'s avatar`}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-info text-white flex items-center justify-center text-sm font-semibold">
                              {coach.userData.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className='flex flex-col gap-1'>
                            <div className="font-medium">
                              {coach.userData.name} {coach.userData.surname}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                          <div className="flex flex-col gap-1">
                            {(() => {
                              const org = getCoachOrganisation(coach.id);
                              return org ? (
                                <div className="flex flex-col gap-1">
                                  <span className="text-sm">{org.name}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-base-content/50">No Organisation</span>
                              );
                            })()}
                          </div>
                      </td>
                      <td>{coach.userData.email}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <span className={`badge h-6 border-none uppercase text-[10px] ${coach.subscription?.subscriptionType === 'coach' ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-base-300 text-base-content/70'}`}>
                            {coach.subscription?.subscriptionType || 'none'}
                          </span>
                          { coach.subscription?.subscriptionType !== 'default' &&
                            <span className={`badge h-6 border-none uppercase text-[10px] ${coach.subscription?.subscriptionVia === 'stripe' ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400' : 'bg-base-300 text-base-content/70'}`}>
                              {coach.subscription?.subscriptionVia}
                            </span> 
                          }
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {(() => {
                              const org = getCoachOrganisation(coach.id);
                              return org ? (
                                <>
                                  <HiUserGroup className="w-4 h-4 text-base-content/60" />
                                  <span className="text-sm">{org.members.length}</span>
                                </>
                              ) : (
                                 <>
                                  <HiUserGroup className="w-4 h-4 text-base-content/60" />
                                  <span className="text-sm">0</span>
                                </>
                              );
                            })()}
                        </div>
                      </td>
                      <td className="text-sm">
                        
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Coach Details Modal */}
      {selectedCoach && (
        <CoachDetailsModal
          coach={selectedCoach}
          organisation={getCoachOrganisation(selectedCoach.id)}
          onClose={() => setSelectedCoach(null)}
          onUpdate={refreshData}
        />
      )}
    </div>
  );
};

export default CoachesCornerManagement;
